// A course's length is a total in minutes. Days only describe how it is delivered, so the total is split
// across them when a booking or session is created.

export type CourseDuration = {
  durationMinutes: number | null
  defaultDays?: number | null
  durationLabel?: string | null
}

function pluralise(n: number, word: string) {
  return `${n} ${word}${n === 1 ? '' : 's'}`
}

/** "45 minutes", "1 hour", "6 hours", "1 hour 30 minutes". */
export function formatMinutes(total: number): string {
  if (total < 60) return pluralise(total, 'minute')
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return rest === 0 ? pluralise(hours, 'hour') : `${pluralise(hours, 'hour')} ${pluralise(rest, 'minute')}`
}

/** "45m", "6h", "1h 30m". */
export function formatMinutesShort(total: number): string {
  if (total < 60) return `${total}m`
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
}

/**
 * How a course's length reads: the course's own wording when it has one, otherwise "6 hours" or "3 days (18 hours)".
 * Returns null when the course has no duration and no wording.
 */
export function formatCourseDuration(course: CourseDuration, opts: { compact?: boolean } = {}): string | null {
  const label = course.durationLabel?.trim()
  if (label) return label
  if (course.durationMinutes == null) return null
  const total = (opts.compact ? formatMinutesShort : formatMinutes)(course.durationMinutes)
  const days = course.defaultDays ?? 1
  return days > 1 ? `${days} days (${total})` : total
}

/** Splits a total across days as evenly as possible, in 5 minute steps where the days are long enough. */
export function splitMinutes(total: number, days: number): number[] {
  const n = Math.max(1, Math.floor(days))
  const step = total / n >= 10 ? 5 : 1
  const base = Math.max(step, Math.round(total / n / step) * step)
  const parts = Array<number>(n).fill(base)
  parts[n - 1] = Math.max(1, total - base * (n - 1))
  return parts
}

/** "09:00" plus 90 minutes is "10:30". A day that would run past midnight stops at 23:59. */
export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return ''
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 59)
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function toIsoDate(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The next weekday after an ISO date ("YYYY-MM-DD"), skipping Saturday and Sunday. */
export function nextWorkingDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  if (!y || !m || !d) return ''
  const date = new Date(y, m - 1, d)
  do {
    date.setDate(date.getDate() + 1)
  } while (date.getDay() === 0 || date.getDay() === 6)
  return toIsoDate(date)
}

export type PlannedDay = { date: string; startTime: string; endTime: string }

/**
 * The days a new booking or session starts with: the course's default number of days, the first on `firstDate`
 * (blank if not chosen yet), later days on the next working days, each starting at `startTime` with an equal share
 * of the course's total time. A course with no duration leaves end times blank.
 */
export function planDays(course: CourseDuration, firstDate: string, startTime: string): PlannedDay[] {
  const days = Math.max(1, course.defaultDays ?? 1)
  const parts = course.durationMinutes != null ? splitMinutes(course.durationMinutes, days) : null
  const plan: PlannedDay[] = []
  let date = firstDate
  for (let i = 0; i < days; i++) {
    if (i > 0) date = date ? nextWorkingDay(date) : ''
    plan.push({
      date,
      startTime,
      endTime: parts ? addMinutesToTime(startTime, parts[i]) : '',
    })
  }
  return plan
}

export type BookingDaysPayload = {
  startTime: string | null
  endTime: string | null
  additionalDays: { date: string; startTime: string | null; endTime: string | null }[]
}

/**
 * The times and extra days a new booking is created with, from its course. A course with no duration and one day
 * leaves the booking date-only, as before.
 */
export function bookingDaysFor(course: CourseDuration | undefined, firstDate: string, startTime = '09:00'): BookingDaysPayload {
  const none: BookingDaysPayload = { startTime: null, endTime: null, additionalDays: [] }
  if (!course || !firstDate) return none
  if (course.durationMinutes == null && (course.defaultDays ?? 1) <= 1) return none
  const [first, ...rest] = planDays(course, firstDate, startTime)
  return {
    startTime: first.startTime,
    endTime: first.endTime || null,
    additionalDays: rest.map(d => ({ date: d.date, startTime: d.startTime, endTime: d.endTime || null })),
  }
}

/** A sentence telling the admin what a new booking will be created with, or null when it is date-only. */
export function describeBookingDays(course: CourseDuration | undefined, firstDate: string, startTime = '09:00'): string | null {
  const plan = bookingDaysFor(course, firstDate, startTime)
  if (plan.startTime == null) return null
  const days = 1 + plan.additionalDays.length
  const times = plan.endTime ? `${plan.startTime} to ${plan.endTime}` : `from ${plan.startTime}`
  return days === 1
    ? `Created for ${times}. You can change this on the booking.`
    : `Created as ${days} days on the next working days, ${times} each day. You can change this on the booking.`
}
